import AsyncStorage from '@react-native-async-storage/async-storage';

export interface Waypoint {
  latitude: number;
  longitude: number;
  name: string;
  description: string;
}

export type WaypointCategory = 'fishing' | 'wrecks' | 'oilgas';

interface WaypointSearchOptions {
  searchTerm?: string;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  limit?: number;
  offset?: number;
  category?: WaypointCategory;
}

class WaypointService {
  private waypointsByCategory: Map<WaypointCategory, Waypoint[]> = new Map();
  private loadingPromises: Map<WaypointCategory, Promise<Waypoint[]>> = new Map();
  private readonly CACHE_KEY_PREFIX = 'waypoints_cache_';
  private readonly CHUNK_SIZE = 100;

  private async loadWaypoints(category: WaypointCategory): Promise<Waypoint[]> {
    const cached = this.waypointsByCategory.get(category);
    if (cached) {
      return cached;
    }

    const loadingPromise = this.loadingPromises.get(category);
    if (loadingPromise) {
      return loadingPromise;
    }

    const promise = this.loadWaypointsFromSource(category);
    this.loadingPromises.set(category, promise);
    
    const waypoints = await promise;
    this.waypointsByCategory.set(category, waypoints);
    this.loadingPromises.delete(category);
    
    return waypoints;
  }

  private async loadWaypointsFromSource(category: WaypointCategory): Promise<Waypoint[]> {
    try {
      // Temporarily disable cache to force reload from JSON file
      // const cached = await AsyncStorage.getItem(cacheKey);
      // if (cached) {
      //   const data = JSON.parse(cached);
      //   if (data && Array.isArray(data) && data.length > 0) {
      //     console.log(`Loaded ${data.length} ${category} waypoints from cache`);
      //     return data;
      //   }
      // }

      let waypointsModule;
      switch (category) {
        case 'fishing': {
          const [fishingModule, destinModule] = await Promise.all([
            import('./OceanInsightFishingSpots.json'),
            import('./OceanInsightDestinWaypoints.json'),
          ]);
          const fishingWaypoints = fishingModule.default || fishingModule;
          const destinWaypoints = destinModule.default || destinModule;
          return [...fishingWaypoints, ...destinWaypoints];
        }
        case 'wrecks':
          waypointsModule = await import('./OceanInsightWaypointWrecks.json');
          break;
        case 'oilgas':
          waypointsModule = await import('./OceanInsightOilGasPlatforms.json');
          break;
        default:
          throw new Error(`Unknown waypoint category: ${category}`);
      }
      
      const waypoints = waypointsModule.default || waypointsModule;

      // Caching disabled - data is bundled with app and loads quickly from JSON
      // Caching large datasets (wrecks) was causing QuotaExceededError

      console.log(`Loaded ${waypoints.length} ${category} waypoints from file`);
      return waypoints;
    } catch (error) {
      console.error(`Error loading ${category} waypoints:`, error);
      return [];
    }
  }

  private async cacheWaypoints(waypoints: Waypoint[], category: WaypointCategory): Promise<void> {
    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}${category}`;
      await AsyncStorage.setItem(cacheKey, JSON.stringify(waypoints));
      console.log(`${category} waypoints cached successfully`);
    } catch (error) {
      console.error(`Error caching ${category} waypoints:`, error);
    }
  }

  async searchWaypoints(options: WaypointSearchOptions = {}): Promise<{
    waypoints: Waypoint[];
    total: number;
    hasMore: boolean;
  }> {
    const {
      searchTerm = '',
      bounds,
      limit = this.CHUNK_SIZE,
      offset = 0,
      category
    } = options;

    let allWaypoints: Waypoint[] = [];
    
    if (category) {
      allWaypoints = await this.loadWaypoints(category);
    } else {
      // Load all categories if no specific category is specified
      const [fishing, wrecks, oilgas] = await Promise.all([
        this.loadWaypoints('fishing'),
        this.loadWaypoints('wrecks'),
        this.loadWaypoints('oilgas')
      ]);
      allWaypoints = [...fishing, ...wrecks, ...oilgas];
    }
    
    let filtered = allWaypoints;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(wp => 
        wp.name.toLowerCase().includes(term) ||
        wp.description?.toLowerCase().includes(term)
      );
    }

    if (bounds) {
      filtered = filtered.filter(wp => 
        wp.latitude >= bounds.south &&
        wp.latitude <= bounds.north &&
        wp.longitude >= bounds.west &&
        wp.longitude <= bounds.east
      );
    }

    const total = filtered.length;
    const paginatedWaypoints = filtered.slice(offset, offset + limit);
    const hasMore = offset + limit < total;

    return {
      waypoints: paginatedWaypoints,
      total,
      hasMore
    };
  }

  async getNearbyWaypoints(
    latitude: number, 
    longitude: number, 
    radiusNm: number = 10,
    limit: number = 20,
    category?: WaypointCategory
  ): Promise<Waypoint[]> {
    let allWaypoints: Waypoint[] = [];
    
    if (category) {
      allWaypoints = await this.loadWaypoints(category);
    } else {
      // Load all categories if no specific category is specified
      const [fishing, wrecks, oilgas] = await Promise.all([
        this.loadWaypoints('fishing'),
        this.loadWaypoints('wrecks'),
        this.loadWaypoints('oilgas')
      ]);
      allWaypoints = [...fishing, ...wrecks, ...oilgas];
    }
    
    const waypointsWithDistance = allWaypoints.map(wp => ({
      ...wp,
      distance: this.calculateDistance(latitude, longitude, wp.latitude, wp.longitude)
    }));

    return waypointsWithDistance
      .filter(wp => wp.distance <= radiusNm)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, limit)
      .map(({ distance, ...wp }) => wp);
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3440.065;
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI/180);
  }

  async getTotalCount(category?: WaypointCategory): Promise<number> {
    if (category) {
      const waypoints = await this.loadWaypoints(category);
      return waypoints.length;
    } else {
      const [fishing, wrecks, oilgas] = await Promise.all([
        this.loadWaypoints('fishing'),
        this.loadWaypoints('wrecks'),
        this.loadWaypoints('oilgas')
      ]);
      return fishing.length + wrecks.length + oilgas.length;
    }
  }

  clearCache(category?: WaypointCategory): void {
    if (category) {
      this.waypointsByCategory.delete(category);
      this.loadingPromises.delete(category);
      const cacheKey = `${this.CACHE_KEY_PREFIX}${category}`;
      AsyncStorage.removeItem(cacheKey).catch(console.error);
    } else {
      this.waypointsByCategory.clear();
      this.loadingPromises.clear();
      // Clear all category caches
      const categories: WaypointCategory[] = ['fishing', 'wrecks', 'oilgas'];
      categories.forEach(cat => {
        const cacheKey = `${this.CACHE_KEY_PREFIX}${cat}`;
        AsyncStorage.removeItem(cacheKey).catch(console.error);
      });
    }
  }
}

export const waypointService = new WaypointService();
